'use client';

/**
 * Campervan / motorhome LIVE search — the real thing.
 *
 * Backed by /api/campervan-hire/search, which calls DriveAway's motorhome
 * engine (book-my-ride) for genuine campervan availability + pricing. Unlike
 * the car surface, every card here is an actual campervan/motorhome. "Select"
 * hands off to DriveAway's motorhome checkout (conversion=CAMPAUS).
 *
 * Renders inside the `.hire` scope on /campervan-hire, so it uses the page's
 * brand tokens (--ink, --terra, --sand, --line, --hair, font-heading).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

const API_BASE =
  (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

// The depots DriveAway's motorhome engine actually serves. Anything else is
// rejected by the API with a clear message — don't offer a city we can't price
// (Gold Coast was listed here but has no motorhome depot).
const DEPOT_CITIES = [
  'Sydney', 'Melbourne', 'Brisbane', 'Cairns', 'Adelaide',
  'Perth', 'Darwin', 'Broome', 'Alice Springs', 'Hobart',
];
const QUICK_CITIES = ['Melbourne', 'Sydney', 'Brisbane', 'Cairns', 'Perth'];

type Offer = {
  brand: string;
  brand_logo: string | null;
  name: string;
  category: string | null;
  image: string | null;
  sleeps: number | null;
  seats: number | null;
  four_wd: boolean;
  price_aud: number | null;
  total_aud: number | null;
  currency: string;
  rental_days: number | null;
  available: boolean;
  tags: string[];
  /** Per-vehicle DriveAway customize deep link (dates + vehicle prefilled). */
  booking_url?: string | null;
};
type SearchResult = {
  offers: Offer[];
  offer_count: number;
  cheapest_aud: number | null;
  pickup_city: string;
  /** Set only on a one-way search (relocation) — else null. */
  dropoff_city?: string | null;
  one_way?: boolean;
  rental_days: number | null;
  booking_url: string;
};

function isoPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}
function nightsBetween(a: string, b: string): number {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(1, Math.round(ms / 86400000));
}

export function CampervanSearch() {
  const [pickupCity, setPickupCity] = useState('Melbourne');
  // One-way / relocation hire: DriveAway returns only the operators that allow
  // the requested drop-off, so this genuinely changes the fleet you see.
  const [oneWay, setOneWay] = useState(false);
  const [dropoffCity, setDropoffCity] = useState('');
  const [pickupDate, setPickupDate] = useState(isoPlusDays(21));
  const [dropoffDate, setDropoffDate] = useState(isoPlusDays(26));
  const [travellers, setTravellers] = useState(2);
  const [driverAge, setDriverAge] = useState(35);

  const [minSleeps, setMinSleeps] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  // Pre-fill from trip-planner / AI-chat deep-links (?from=YYYY-MM-DD&to=…
  // &city=…) so "hire one for the trip" opens the search with the planned
  // dates. When ALL THREE are present the search auto-runs — the chat's
  // "compare all campervans" handoff must land on results, not a blank form.
  const autoRunRef = useRef<{ city: string; from: string; to: string } | null>(null);
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const from = q.get('from');
      const to = q.get('to');
      const city = q.get('city');
      const isIso = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
      if (isIso(from)) setPickupDate(from);
      if (isIso(to) && (!isIso(from) || to > from)) setDropoffDate(to);
      if (city?.trim()) setPickupCity(city.trim());
      if (isIso(from) && isIso(to) && to > from && city?.trim()) {
        autoRunRef.current = { city: city.trim(), from, to };
      }
    } catch {
      /* no query string — defaults stand */
    }
  }, []);

  // Fires once the prefilled state has actually landed (guarding on the
  // exact values avoids racing the setState above with stale defaults).
  useEffect(() => {
    const want = autoRunRef.current;
    if (!want) return;
    if (pickupCity === want.city && pickupDate === want.from && dropoffDate === want.to) {
      autoRunRef.current = null;
      void runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupCity, pickupDate, dropoffDate]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searchedCity, setSearchedCity] = useState('');

  async function runSearch() {
    setError(null);
    if (!pickupCity.trim()) { setError('Enter a pickup city.'); return; }
    if (dropoffDate <= pickupDate) { setError('Drop-off must be after pickup.'); return; }
    if (oneWay && !dropoffCity.trim()) { setError('Choose a drop-off city, or untick one-way.'); return; }
    setLoading(true);
    setResult(null);
    try {
      const params = new URLSearchParams({
        pickup_city: pickupCity.trim(),
        pickup_date: pickupDate,
        dropoff_date: dropoffDate,
        driver_age: String(driverAge),
        passengers: String(travellers),
      });
      if (oneWay && dropoffCity.trim()) params.set('dropoff_city', dropoffCity.trim());
      const res = await fetch(`${API_BASE}/campervan-hire/search?${params}`);
      if (!res.ok) {
        // 400 carries a specific, user-fixable message (e.g. unknown depot).
        let msg = res.status === 503
          ? 'Live availability is temporarily unavailable — please try again in a moment.'
          : 'Search failed. Please try again.';
        if (res.status === 400) {
          try {
            const body = await res.json();
            if (body?.error) msg = String(body.error);
          } catch { /* keep the default message */ }
        }
        throw new Error(msg);
      }
      const data: SearchResult = await res.json();
      setResult(data);
      setSearchedCity(pickupCity.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  const nights = result?.rental_days || nightsBetween(pickupDate, dropoffDate);
  const offers = useMemo(() => {
    let list = result?.offers ?? [];
    if (minSleeps) list = list.filter((o) => (o.sleeps ?? 0) >= minSleeps);
    return list;
  }, [result, minSleeps]);

  const money = (n: number | null | undefined) =>
    n == null ? '—' : `$${Math.round(n).toLocaleString('en-AU')}`;

  return (
    <div>
      {/* Search card */}
      <form
        onSubmit={(e) => { e.preventDefault(); void runSearch(); }}
        className="rounded-2xl bg-[var(--sand-2)] p-4 sm:p-5 cshadow"
      >
        <div className="grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-end">
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-soft)]">Pick-up city</span>
            <input
              list="cv-cities"
              value={pickupCity}
              onChange={(e) => setPickupCity(e.target.value)}
              placeholder="e.g. Melbourne"
              className="w-full rounded-lg border border-[var(--line)] bg-[var(--sand)] px-3 py-2.5 text-[15px] text-[var(--ink)] outline-none focus:border-[var(--terra)]"
            />
            <datalist id="cv-cities">{DEPOT_CITIES.map((c) => <option key={c} value={c} />)}</datalist>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-soft)]">Pick-up</span>
            <input type="date" min={isoPlusDays(1)} value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
              className="w-full rounded-lg border border-[var(--line)] bg-[var(--sand)] px-3 py-2.5 text-[15px] text-[var(--ink)] outline-none focus:border-[var(--terra)]" />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ink-soft)]">Drop-off</span>
            <input type="date" min={pickupDate} value={dropoffDate} onChange={(e) => setDropoffDate(e.target.value)}
              className="w-full rounded-lg border border-[var(--line)] bg-[var(--sand)] px-3 py-2.5 text-[15px] text-[var(--ink)] outline-none focus:border-[var(--terra)]" />
          </label>
          <button type="submit" disabled={loading}
            className="h-[46px] rounded-lg bg-[var(--terra)] px-6 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60">
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-[13px] text-[var(--ink-soft)]">
            <input
              type="checkbox"
              checked={oneWay}
              onChange={(e) => {
                setOneWay(e.target.checked);
                if (!e.target.checked) setDropoffCity('');
              }}
            />
            Drop off in a different city
          </label>
          {oneWay && (
            <label className="flex items-center gap-2 text-[13px] text-[var(--ink-soft)]">
              Drop-off city
              <input
                list="cv-cities"
                value={dropoffCity}
                onChange={(e) => setDropoffCity(e.target.value)}
                placeholder="e.g. Cairns"
                className="w-36 rounded-md border border-[var(--line)] bg-[var(--sand)] px-2 py-1 text-[var(--ink)] outline-none focus:border-[var(--terra)]"
              />
            </label>
          )}
          <label className="flex items-center gap-2 text-[13px] text-[var(--ink-soft)]">
            Travellers
            <select value={travellers} onChange={(e) => setTravellers(Number(e.target.value))}
              className="rounded-md border border-[var(--line)] bg-[var(--sand)] px-2 py-1 text-[var(--ink)]">
              {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-[var(--ink-soft)]">
            Driver age
            <input type="number" min={18} max={99} value={driverAge}
              onChange={(e) => setDriverAge(Number(e.target.value) || 35)}
              className="w-16 rounded-md border border-[var(--line)] bg-[var(--sand)] px-2 py-1 text-[var(--ink)]" />
          </label>
          <span className="hidden text-[13px] text-[var(--ink-soft)] sm:inline">·</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[var(--terra)]">Popular</span>
            {QUICK_CITIES.map((c) => (
              <button key={c} type="button"
                onClick={() => { setPickupCity(c); }}
                className={`rounded-full border px-3 py-1 text-[12.5px] transition ${pickupCity === c ? 'border-[var(--terra)] bg-[rgba(56,114,229,0.08)] text-[var(--terra)]' : 'border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--terra)]'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </form>

      {error && (
        <div className="mt-4 rounded-xl border border-[rgba(180,60,60,0.3)] bg-[rgba(180,60,60,0.06)] px-4 py-3 text-sm text-[#a33]">{error}</div>
      )}
      {loading && (
        <div className="mt-6 rounded-2xl border border-[var(--line)] bg-[var(--sand-2)] p-6 text-center text-sm text-[var(--ink-soft)]">
          Checking live availability across Jucy, Britz, Maui, Apollo, Mighty, Cheapa &amp; Star RV…
        </div>
      )}

      {result && !loading && (
        result.offers.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-[var(--line)] bg-[var(--sand-2)] p-6 text-sm text-[var(--ink-soft)]">
            {result.one_way && result.dropoff_city ? (
              <>
                No campervans available for {searchedCity} → {result.dropoff_city} on those dates. Not every operator
                allows that one-way trip — try another drop-off city, different dates, or untick
                “drop off in a different city”.
              </>
            ) : (
              <>
                No campervans available from {searchedCity} for those dates. Try another depot city (Melbourne, Sydney,
                Brisbane, Cairns, Perth…) or different dates.
              </>
            )}
          </div>
        ) : (
          <>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading text-xl text-[var(--ink)]">
                {offers.length} campervan{offers.length === 1 ? '' : 's'}
                <span className="ml-1 text-[15px] text-[var(--ink-soft)]">
                  · {result.one_way && result.dropoff_city
                      ? `${searchedCity} → ${result.dropoff_city}`
                      : searchedCity}
                  {' '}· {nights} night{nights === 1 ? '' : 's'}
                </span>
              </h3>
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] text-[var(--ink-soft)]">Sleeps</span>
                {[2, 4, 6].map((n) => (
                  <button key={n} type="button" onClick={() => setMinSleeps(minSleeps === n ? null : n)}
                    className={`rounded-full border px-2.5 py-1 text-[12.5px] transition ${minSleeps === n ? 'border-[var(--terra)] bg-[rgba(56,114,229,0.08)] text-[var(--terra)]' : 'border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--terra)]'}`}>
                    {n}{n === 6 ? '+' : ''}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {offers.map((o, i) => (
                <div key={`${o.name}-${i}`} className="flex flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--sand-2)]">
                  <div className="relative h-[168px] bg-[var(--sand)]">
                    {o.image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={o.image} alt={`${o.brand} ${o.name}`} loading="lazy" className="h-full w-full object-contain p-3" />
                    ) : <div className="h-full w-full" />}
                    <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold ${o.available ? 'bg-[rgba(47,158,107,0.14)] text-[#2f9e6b]' : 'bg-[rgba(28,30,32,0.08)] text-[var(--ink-soft)]'}`}>
                      {o.available ? 'Available' : 'On request'}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-center gap-2">
                      {o.brand_logo && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={o.brand_logo} alt={o.brand} className="h-4 w-auto" />
                      )}
                      <span className="text-[12px] text-[var(--ink-soft)]">{o.brand}</span>
                    </div>
                    <h4 className="font-heading mt-0.5 text-[17px] text-[var(--ink)]">{o.name}</h4>
                    <div className="mt-1 text-[13px] text-[var(--ink-soft)]">
                      Sleeps {o.sleeps ?? '—'} · seats {o.seats ?? '—'}{o.category ? ` · ${o.category}` : ''}
                    </div>
                    {o.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {o.tags.slice(0, 4).map((t) => (
                          <span key={t} className="rounded-md bg-[var(--sand)] px-2 py-0.5 text-[11.5px] text-[var(--ink-soft)]">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-auto flex items-end justify-between pt-4">
                      <div>
                        <div className="font-heading text-[22px] leading-none text-[var(--ink)]">{money(o.price_aud)}</div>
                        <div className="mt-1 text-[12px] text-[var(--ink-soft)]">
                          total · {nights} night{nights === 1 ? '' : 's'}
                          {o.price_aud ? ` · ${money(Math.round(o.price_aud / Math.max(1, nights)))}/night` : ''}
                        </div>
                      </div>
                      <a href={o.booking_url || result.booking_url} target="_blank" rel="noopener noreferrer sponsored"
                        className="rounded-lg bg-[var(--terra)] px-4 py-2.5 text-[13px] font-semibold text-white transition hover:opacity-90">
                        Select
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[12.5px] text-[var(--ink-soft)]">
              Live prices &amp; availability from DriveAway. Headline price is the base rental; fees (LPG, one-way, etc.)
              and exact inclusions are confirmed at checkout on DriveAway.
            </p>
          </>
        )
      )}
    </div>
  );
}
